<template>
  <div @click="selectedItem = null">
    <breadcrumbs :home="recurring ? 'Recurring inventory receipt template' : 'Inventory receipt'"/>
    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row v-if="recurring">
            <v-col><recurring-transaction v-model="recurringTransaction"/></v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="5">
              <v-row>
                <v-col>
                  <v-autocomplete v-model="inventoryReceipt.vendorId" outlined dense label="Supplier" :rules="[supplier]"
                                  :items="vendors" item-text="name" item-value="id" @change="changeSupplier" no-data-text="No suppliers found"
                                  :hint="vendor && vendor.suppliesInBond?'Supplies alcohol in-bond':''" persistent-hint>
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="supplierCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new supplier
                      </div>
                    </template>
                    <template v-slot:item="{item}">
                      <v-list-item-content>{{item.name}}</v-list-item-content>
                      <v-list-item-action class="text-caption">{{item.suppliesInBond?'Supplies alcohol':''}}</v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="mb-4">
                  <v-textarea label="Mailing address" v-model="inventoryReceipt.vendorAddress" outlined dense no-resize :rows="4" :rules="[fieldRules.maxLength(250)]" hide-details="auto"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="6" offset-sm="1">
              <v-row dense>
                <v-col cols="7" sm="5">
                  <date-picker v-model="inventoryReceipt.date" @change="changeDate()" v-if="!recurring"/>
                </v-col>
                <v-col cols="5" offset-sm="2">
                  <v-text-field label="Bill #" v-model="inventoryReceipt.billNumber" outlined dense :rules="[fieldRules.maxLength(20)]" counter="20"/>
                </v-col>
              </v-row>
              <v-row dense v-if="connectedToAccounting && values">
                <v-col cols="5">
                  <v-autocomplete label="QuickBooks expense" v-model="inventoryReceipt.paymentMethodId" :items="paymentMethods" item-value="id" item-text="name" outlined dense
                            @change="eligibleAccounts.length > 0 ? inventoryReceipt.accountRef=eligibleAccounts[0].ref : ''">
                    <template v-slot:item="{item}">
                      <v-list-item-content>{{item.name}}</v-list-item-content>
                      <v-list-item-action class="text-caption">{{item.id===''?'Bill':(item.id===null?'':'Expense')}}</v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col>
                  <account-picker v-if="!!inventoryReceipt.paymentMethodId && eligibleAccounts.length>1"
                                  v-model="inventoryReceipt.accountRef"
                                  :label="inventoryReceipt.paymentMethodId==='' ? 'A/P' : 'Account'"
                                  :accounts="eligibleAccounts"/>
                </v-col>
              </v-row>
              <v-row dense v-else-if="connectedToAccounting && (inventoryReceiptId==null || inventoryReceipt.pendingReview)">
                <v-col class="warning--text mb-4">Inventory receipt will have status of pending review</v-col>
              </v-row>
              <v-row dense>
                <v-col cols="7" sm="5" v-if="connectedToAccounting && inventoryReceipt.paymentMethodId==='' && values">
                  <date-picker label="QuickBooks due date" v-model="inventoryReceipt.due" :disabled="inventoryReceipt.paymentMethodId==null" date-only v-if="!recurring"/>
                </v-col>
                <v-col cols="12" md="7" v-if="sites.length>1">
                  <v-autocomplete v-model="inventoryReceipt.siteId" outlined dense label="Receive to site"
                                  :items="sites" item-text="name" item-value="id"/>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="1" v-if="$vuetify.breakpoint.mobile">
              <scan-code @code="scan" :items="items"/>
            </v-col>
            <v-spacer/>
            <v-spacer/>
            <v-col cols="12" sm="5" lg="3">
              <v-switch label="Amounts are inclusive of tax" v-model="inventoryReceipt.taxInclusive" dense hide-details class="mt-0" @change="toggleTaxInclusive()"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <outlined-block v-model="inventoryReceipt.lines" :rules="[lines]" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="inventoryReceipt.lines" item-key="key" no-data-text="There must be at least one item to receive"
                              :headers="headers"
                              :item-class="item => item.key === selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              class="no-sort" ref="lineTableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:header.add>
                    <scan-code @code="scan" :items="items"/>
                  </template>
                  <template v-slot:item.add="{index}">
                    <v-icon :disabled="!edit" class="handle mt-n3">mdi-drag</v-icon>
                    <v-btn :disabled="!edit" class="mt-n3" icon @click.stop="deleteLine(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <template v-if="selectedItem === item.key">
                      <multi-level-select :menuData="lineTypes" :first.sync="item.type" :second.sync="item.id" caption @change="setLineDetails(item,false)"
                                    @addSpirit="inputLine=item;spiritCreate=true"
                                    @addWine="inputLine=item;wineCreate=true"
                                    @addNonExcise="inputLine=item;nonExciseCreate=true"
                                    @addItem="inputLine=item;itemCreate=true" outlined label="Item"/>
                      <v-select v-if="item.type==='Product' && !isProductNonExcise(item)"
                                class="text-caption" v-model="item.productAdditionType" outlined label="Excise" dense
                                :items="exciseTypes"/>
                      <template v-else-if="!!item.type && item.type!=='Item'">
                        <tank-select v-model="item.tankId" :tanks="tanks" :hint="inventoryReceiptId===undefined" :required="false" outlined label="Tank"/>
                        <v-checkbox dense v-model="item.importedAlcohol" label="Imported" hide-details v-if="item.type !== 'NonExcise'">
                          <template v-slot:label><span class="text-caption">Imported</span></template>
                        </v-checkbox>
                      </template>
                    </template>
                    <div v-else class="pl-1">
                      <div :class="{'error--text':!item.type || !item.id}">{{itemDescription(item)}}</div>
                      <div v-if="item.type==='Product' && !isProductNonExcise(item)" class="text-caption">
                        {{exciseTypes.find(type => type.value === item.productAdditionType).text}}
                      </div>
                      <template v-else-if="!!item.type && item.type!=='Item'">
                        <div class="text-caption">{{tankName(item.tankId)}}</div>
                        <div class="text-caption" v-if="item.importedAlcohol">Imported</div>
                      </template>
                    </div>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input prefix="" :suffix="suffix(item)" :precision="precision(item)" outlined label="Quantity"
                                            class="text-caption" v-model="item.quantity" @change="updateAmount(item)" :no-minimum="item.type==='Item'"/>
                      <decimal-number-input v-show="item.type==='Spirit' && trackLitres"
                                            label="Volume" v-model="item.litres" prefix="" class="text-caption"
                                            suffix="litres" :precision="3"/>
                      <decimal-number-input v-show="item.type==='Spirit' && trackKg"
                                            label="Weight" v-model="item.kg" prefix="" class="text-caption"
                                            suffix="kg" :precision="3"/>
                      <decimal-number-input v-show="item.type==='Wine' || item.type==='Spirit' && trackStrength"
                                            label="Strength" v-model="item.strength" prefix="" class="text-caption" :max="100"
                                            suffix="% alc./vol." :precision="2"/>
                    </template>
                    <template v-else>
                      <div class="pr-1" :class="{'error--text':item.quantity < 0 && item.type!=='Item'}">{{item.quantity | number(precision(item))}} {{suffix(item)}}</div>
                      <div v-show="item.type==='Spirit' && trackLitres"
                           class="pr-1" :class="{'error--text':item.litres < 0}">{{item.litres | number(3)}} litres</div>
                      <div v-show="item.type==='Spirit' && trackKg"
                           class="pr-1" :class="{'error--text':item.kg < 0}">{{item.kg | number(2)}} kg</div>
                      <div v-show="item.type==='Wine' || item.type==='Spirit' && trackStrength"
                           class="pr-1" :class="{'error--text':item.strength < 0 || item.strength>100}">{{item.strength | number(2)}}% alc./vol.</div>
                    </template>
                  </template>
                  <template v-slot:item.po="{item}">
                    <v-select v-if="selectedItem === item.key" :items="purchaseOrdersForLine(item)" item-text="poNumber" item-value="purchaseOrderId" :return-object="false" v-model="item.purchaseOrderId"
                              no-data-text="" outlined label="PO #"
                              class="text-caption" dense @change="changedPO(item)"/>
                    <div v-else class="mr-1">{{poForLine(item)}}</div>
                  </template>
                  <template v-slot:item.lot="{item}">
                    <template v-if="item.lot!=null">
                      <v-text-field v-if="selectedItem === item.key" v-model="item.lot" class="text-caption" dense :rules="[fieldRules.required,fieldRules.maxLength(20)]"
                                    hint="From supplier or arbitrary" outlined label="Lot"/>
                      <div v-else class="ml-1" :class="{'error--text':!item.lot}">{{item.lot || 'Required'}}</div>
                    </template>
                    <template v-else>n/a</template>
                  </template>
                  <template v-slot:item.amount="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Amount" v-model="item.amount" class="text-caption" @change="amountChanged(item)" :no-minimum="item.type==='Item'"/>
                      <div v-else class="pr-1" :class="{'error--text':item.amount<0 && item.type!=='Item'}">{{item.amount | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.taxes="{item}">
                    <template v-if="!item.items">
                      <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" outlined v-model="item.taxCodeId" :rules="[connectedToAccounting?fieldRules.required:true]"
                                :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTaxes()"/>
                      <div v-else class="pl-1" :class="{'error--text':connectedToAccounting && !item.taxCodeId}">{{itemTax(item)}}</div>
                    </template>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab color="primary" x-small @click.stop="addLine" :disabled="!edit"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" order="2" order-sm="1">
              <v-container class="pa-0">
                <v-row>
                  <v-col>
                    <v-textarea rows="2" label="Private note" v-model="inventoryReceipt.privateNote" outlined dense :rules="[fieldRules.maxLength(1000)]"/>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="1">
                    <attachment v-model="inventoryReceipt.attachmentURL" :read-only="!edit" @change="inventoryReceipt.attachmentUpload=inventoryReceipt.attachmentURL" @delete="inventoryReceipt.attachmentDelete=true"/>
                  </v-col>
<!--
  FUTURE ENHANCEMENT ... maybe - to allow for payment but no actual inventory receipt
  ...this may be better handled by created a credit in QBO and then dealing with paying
  a bill created on actual inventory receipt with A/P workflow in QBO
                  <v-col cols="3" v-if="connectedToAccounting && inventoryReceipt.paymentRef">
                    <v-tooltip bottom open-delay="500" max-width="300">
                      <template v-slot:activator="{on}">
                        <v-checkbox v-on="on" hide-details dense class="mt-1">
                          <span slot="label" v-on="on">Waiting</span>
                        </v-checkbox>
                      </template>
                      If selected, goods will not be received in inventory,
                      even though the QuickBooks transaction will be posted.
                      When the goods physically arrive, the inventory receipt
                      must be marked as received to have inventory added.
                    </v-tooltip>
                  </v-col>
                  -->
                  <v-spacer/>
                  <v-col cols="6" sm="4" md="3" v-if="vendor && company.settings.homeCurrency !== vendor.currency">
                    <decimal-number-input class="text-caption" :label="`1 ${vendor.currency} =`" outlined v-model="inventoryReceipt.exchangeRate" prefix="" :suffix="` ${company.settings.homeCurrency}`" :precision="6" hide-details/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" sm="6" order="1" order-sm="2">
              <v-container class="pa-0">
                <v-row dense>
                  <v-spacer/>
                  <v-col cols="8" class="font-weight-medium text-right">Subtotal</v-col>
                  <v-col cols="4" class="text-right pr-6">{{subTotal | currency}}</v-col>
                </v-row>
                <template v-for="tax of inventoryReceipt.taxLines">
                  <v-row dense v-if="tax.name!=='Exempt' && tax.name!=='Out of Scope'" :key="tax.id" align="center">
                    <v-col cols="8" class="font-weight-medium text-right">{{inventoryReceipt.taxInclusive?'Includes ':''}}{{tax.name}} @{{tax.percent}}% on {{tax.onAmount | currency}}</v-col>
                    <v-col cols="3" offset="1" class="text-right pr-6">
                      <decimal-number-input v-model="tax.amount" hide-details no-minimum/>
                    </v-col>
                  </v-row>
                </template>
                <v-row dense>
                  <v-spacer/>
                  <v-col cols="8" class="font-weight-medium text-right">Total {{vendor && company.settings.homeCurrency !== vendor.currency ? `(${vendor.currency})` : ''}}</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total | currency}}</v-col>
                </v-row>
                <v-row dense v-if="vendor && company.settings.homeCurrency !== vendor.currency">
                  <v-spacer/>
                  <v-col cols="8" class="font-weight-medium text-right">Total ({{company.settings.homeCurrency}})</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total*inventoryReceipt.exchangeRate | currency}}</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="!inventoryReceiptId || !inventoryReceipt.recurringTransaction">
          <v-switch v-if="inventoryReceiptId && company.permissions.includes('buying.write') && !originalDraft"
                    v-model="edit" label="Edit" hide-details class="mt-0 mr-4"
                    :disabled="dirty || (!values && !inventoryReceipt.pendingReview && connectedToAccounting)"/>
          <v-switch dense hide-details label="Make recurring" class="mt-0" v-model="recurring"
                    v-if="edit && company.allowRecurringTransactions && (!inventoryReceiptId || !inventoryReceipt.recurringTransaction)" :disabled="onlyRecurring"/>
        </template>
        <v-spacer/>
        <v-checkbox v-if="edit" dense hide-details label="Draft" v-model="inventoryReceipt.draft" class="mr-2 mt-n1"/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save()">Save{{recurring?' template':''}}</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <supplier-quick-create v-model="supplierCreate" @create="newSupplier"/>
    <inventory-items-quick-create v-model="itemCreate" @create="itemCreated"/>
    <spirit-quick-create v-model="spiritCreate" @create="spiritCreated"/>
    <wine-quick-create v-model="wineCreate" @create="wineCreated"/>
    <alcohol-non-excise-quick-create v-model="nonExciseCreate" @create="nonExciseCreated"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns'
import sortableJS from 'sortablejs';
import SupplierQuickCreate from '@/views/purchasing/suppliers/SupplierQuickCreate'
import InventoryItemsQuickCreate from '@/views/inventory/items/InventoryItemsQuickCreate'
import SpiritQuickCreate from '@/views/alcohol/spirits/SpiritQuickCreate'
import WineQuickCreate from '@/views/alcohol/wine/WineQuickCreate'
import AlcoholNonExciseQuickCreate from '@/views/alcohol/nonExcise/AlcoholNonExciseQuickCreate'
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'
import Attachment from '@/components/Attachment'
import createHierarchyList from '@/mixins/createHierarchyList'
import RecurringTransaction from '@/components/RecurringTransaction'

export default {
  name: 'PurchasingInventoryReceiptsEdit',
  mixins: [confirmLeave,createHierarchyList],
  components: {SupplierQuickCreate,InventoryItemsQuickCreate,SpiritQuickCreate,WineQuickCreate,AlcoholNonExciseQuickCreate,TankSelect,Attachment,RecurringTransaction},
  props: {
    inventoryReceiptId: {type:String, required:false},
    poId: {type:String, required:false},
    vendorId: {type:String, required:false},
    itemType: {type:String, required:false},
    itemId: {type:String, required:false},
    copyId: {type:String, required:false},
    onlyRecurring: {type:Boolean, required:false},
    fromRecurringTransactionId: {type:String, required:false},
    date: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      recurring: this.onlyRecurring,
      recurringTransaction: {},
      accounts: [],
      paymentMethods: [{id:'',name:'Bill'},{divider:true}],
      taxes: [],
      vendors: [],
      supplierCreate:false,

      spirits: [],
      wines: [],
      nonExcises: [],
      items: [],
      itemCategories: [],
      products: [],
      productCategories: [],
      allTanks: null,
      inventoryReceipt: {vendorId:'',vendorAddress:'',date:this.date ? `${this.date} 00:00:00` : format(new Date(), 'yyyy-MM-dd HH:mm:ss'),due:this.date ?? format(new Date(), 'yyyy-MM-dd'), draft:false,
                         taxInclusive:false,accountRef:'',siteId:'',lines:[],taxLines:[],privateNote:'',billNumber:'',paymentMethodId:'',exchangeRate:1,qbo:true},
      dirty: false,
      saving: false,
      loading: false,
      originalDraft: false,
      valid: true,
      lines: v => v.length>0 || 'There must be at least one line item',
      supplier: v => v != '' || 'Select a supplier',
      itemValid: id => id !== '' || 'Please select',
      inputLine: null,
      itemCreate: false,
      spiritCreate: false,
      wineCreate: false,
      nonExciseCreate: false,
      selectedItem: null,
      exciseTypes: [{text:'Domestic non-duty-paid packaged',value:0},
                    {text:'Imported non-duty-paid packaged',value:1},
                    {text:'Returned from non-duty-paid sources',value:2}],
      headers: [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                { text: 'Item', value: 'item', width:'30%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                { text: 'Quantity', value: 'quantity' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                { text: 'PO #', value: 'po' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                { text: 'Lot', value: 'lot' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                { text: 'Amount ' + (this.vendor && this.company.settings.homeCurrency !== this.vendor.currency ? this.vendor.currency : ''), value: 'amount' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                { text: 'Tax', value: 'taxes' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true}],
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    trackKg() {
      return this.$store.getters.company.settings.trackKg;
    },
    trackStrength() {
      return this.$store.getters.company.settings.trackStrength;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting && this.company.qboPlan !== 'Start';
    },
    sites() {
      return this.$store.getters.sites(!this.inventoryReceiptId);
    },
    nonExciseSite() {
//      return this.sites.find(s => s.id === this.inventoryReceipt.siteId)?.nonExcise;
return false;
    },
    vendor() {
      return this.vendors.find(vendor => vendor.id === this.inventoryReceipt.vendorId);
    },
    lineTypes() {
      const types = [];

      if (this.itemCategories.length) {
        const categories = this.createHierarchyList(this.itemCategories);
        for (const category of categories) {
          const sub = this.items.filter(item => item.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
        const otherItems = {name:'Other items',id:'x'};
        const leftOver = this.items.filter(item => !item.category?.id);
        if (leftOver.length)
          otherItems.menu = leftOver;
        else
          otherItems.disabled = true;
        types.push({name:'Item',id:'Item',menu:[...categories, {divider:true}, otherItems],actionEvent:'addItem',actionText:'Add new item'});
      }
      else
        types.push({name:'Item',id:'Item',menu:this.items,actionEvent:'addItem',actionText:'Add new item'});

      if (this.vendor?.suppliesInBond && !this.nonExciseSite) {
        types.push({name:'Spirit',id:'Spirit',menu:this.spirits,actionEvent:'addSpirit',actionText:'Add new spirit'});
        types.push({name:'Wine',id:'Wine',menu:this.wines,actionEvent:'addWine',actionText:'Add new wine'});
        lineTypeProducts(this, 'in-bond');
      }
      else
        lineTypeProducts(this, 'duty-paid');

      types.push({name:'Non-excise alcohol',id:'NonExcise',menu:this.nonExcises,actionEvent:'addNonExcise',actionText:'Add new non-excise'});

      return types;

      function lineTypeProducts(_this, suffix) {
        if (_this.products.length) {
          if (_this.productCategories.length) {
            const categories = _this.createHierarchyList(_this.productCategories);
            for (const category of categories) {
              const sub = _this.products.filter(product => product.category?.id === category.id);
              if (sub.length)
                category.menu = sub;
              else
                category.disabled = true;
            }
            types.push({name:`Product (${suffix})`,id:'Product',menu:categories});
            const leftOver = _this.products.filter(product => !product.category?.id);
            if (leftOver.length)
              types[types.length-1].menu.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
          }
          else
            types.push({name:`Product (${suffix})`,id:'Product',menu:_this.products});
        }
      }
    },
    eligibleAccounts() {
      return this.vendor ?
              this.accounts.filter(account => account.currency === this.vendor.currency &&
                                              (this.inventoryReceipt.paymentMethodId === '' && account.type === 'AccountsPayable' ||
                                               this.inventoryReceipt.paymentMethodId !== '' && (account.type === 'Bank' || account.type === 'CreditCard')))
              : [];
    },
    subTotal() {
      return this.inventoryReceipt.lines.reduce((total,line) => total + line.amount, 0)
    },
    total() {
      return this.inventoryReceipt.taxInclusive ? this.subTotal : this.inventoryReceipt.taxLines.reduce((total,tax) => tax.amount + total, 0) + this.subTotal;
    },
    values() {
      return this.$store.getters.company.permissions.includes('values');
    },
    tanks() {
      return [{id:'',name:'Create new bulk container'},{divider:true},
              ...this.allTanks?.filter(tank => tank.contents.siteId === this.inventoryReceipt.siteId)];
    }
  },
  watch: {
    inventoryReceipt: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    }
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.$lazyListLoad(this.productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(this.products, 'products2', 'id,name,category{id},alcohol{type}', 100, !this.inventoryReceiptId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.vendors, 'vendors2', 'id,name,suppliesInBond,currency', 100, !this.inventoryReceiptId ?  [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.items, 'items2', 'id,name,lotTracking,buyingUnits,buyingPrecision,category{id},upc', 100, !this.inventoryReceiptId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.inventoryReceiptId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ? this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.inventoryReceiptId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.inventoryReceiptId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,purchaseRate', 100, this.inventoryReceiptId ? [] : [{field:'active', comparator:'eq', value:'true'},{field:'purchaseRate', comparator:'ne', value:''}]),
      this.$lazyListLoad(this.paymentMethods, 'paymentMethods2', 'id,name', 100, this.inventoryReceiptId ? [] : [{field:'active', comparator:'eq', value:'true'}])
    ]);
    const results = await this.$apiCall(`query PurchasingInventoryReceiptsEdit($accounting:Boolean!, $edit:Boolean!, $id:ID!, $po:Boolean!, $poId:ID!) {
                                              inventoryReceipt(id:$id) @include(if:$edit) {
                                                id,vendorId,vendorAddress,date,due,draft,
                                                taxInclusive,accountRef,privateNote,siteId,paymentMethodId,qbo,billNumber,exchangeRate,pendingReview,attachmentURL,
                                                lines {type,id,tankId,quantity,litres,kg,strength,purchaseOrderId,lot,importedAlcohol,productReturn,amount,taxCodeId}
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                recurringTransaction {
                                                  name,scheduleType,advanceDays,startDate,scheduleInterval
                                                  frequency,dayOfWeek,ordinal,day,month
                                                  end,endDate,endOccurances
                                                }
                                              }
                                              purchaseOrder(id:$poId) @include(if:$po) {
                                                vendorId,vendorAddress,
                                                lines {type,id,quantity,receivedQuantity,unitPrice,amount,taxCodeId}
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                              }
                                              accounts @include(if:$accounting) {
                                                ref,name,type,typeName,currency
                                              }
                                          }`,
                                        {accounting:this.connectedToAccounting,
                                         edit:this.inventoryReceiptId!=undefined || this.copyId != undefined,
                                         id:this.inventoryReceiptId ?? this.copyId ?? '',
                                         po:this.poId!=undefined,poId:this.poId || ''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.paymentMethods.push({divider:true},{id:null,name:'None'});

    if (this.connectedToAccounting) {
      this.accounts = results.data.accounts; // results.data.accounts.filter(account => {return account.type === 'AccountsPayable'});
      if (this.inventoryReceiptId && !this.values && !results.data.inventoryReceipt.pendingReview)
        this.$store.commit('setError', {message:'You do not have permission to make changes to this inventory receipt.', type:'warning'});
//      else if (this.taxes.length === 0)
//        this.$store.commit('setError', {message:'No sales taxes were found. Make sure taxes are setup in QuickBooks and synced with PŪRTRAK.', type:'warning'});
    }

    await this.getTanks();      // Refresh list of tanks

    if (this.inventoryReceiptId || this.copyId) {
      let key = Date.now();
      results.data.inventoryReceipt.lines.forEach(item => {item.key = key++;
                                                           item.tankId = item.tankId || '',
                                                           item.unitPrice = item.amount ? (item.amount / item.quantity) : 0,
                                                           item.productAdditionType = item.importedAlcohol?1:(item.productReturn?2:0)});
      this.inventoryReceipt = results.data.inventoryReceipt;
      if (!this.inventoryReceipt.qbo && !this.inventoryReceipt.draft)
        this.inventoryReceipt.paymentMethodId = null;
      const taxLines = this.inventoryReceipt.taxLines;  // cuz wiped out by setLineDetails below
      for (const line of this.inventoryReceipt.lines) {
        await this.setLineDetails(line, true);
        if (this.inventoryReceipt.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.inventoryReceipt.taxInclusive)
            line.unitPrice += line.unitPrice * tax.purchaseRate/100;
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.inventoryReceipt.taxLines = taxLines;  // cuz wiped out by setLineDetails above
      this.recurring = !!this.inventoryReceipt.recurringTransaction && !this.copyId;
      this.recurringTransaction = this.inventoryReceipt.recurringTransaction ?? {};
      this.originalDraft = this.inventoryReceipt.draft;
      this.edit = this.inventoryReceipt.pendingReview || this.originalDraft || this.recurring;
    }
    if (!this.inventoryReceiptId) {
      if (this.copyId) {
        this.inventoryReceipt.date = `${this.date} 00:00:00` ?? format(new Date(), 'yyyy-MM-dd HH:mm:ss');
        this.inventoryReceipt.due = this.date ?? format(new Date(), 'yyyy-MM-dd');
      }
      this.edit = true;
      this.inventoryReceipt.number = results.data.nextInventoryReceiptNumber;
      this.inventoryReceipt.siteId = this.sites[0].id;
      if (this.poId) {
        this.inventoryReceipt.vendorId = results.data.purchaseOrder.vendorId;
        this.inventoryReceipt.vendorAddress = results.data.purchaseOrder.vendorAddress;
        this.changeSupplier();
        for (const poLine of results.data.purchaseOrder.lines) {
          const quantity = poLine.quantity - poLine.receivedQuantity;
          if (quantity) {
            const amount = poLine.amount ? this.$roundNumber(quantity / poLine.quantity * poLine.amount) : 0;
            const line = {type:poLine.type,id:poLine.id,importedAlcohol:false,quantity,litres:0,kg:0,strength:7,purchaseOrderId:this.poId,amount,taxCodeId:poLine.taxCodeId,lot:null,unitPrice:amount/quantity,tankId:''};
            await this.setLineDetails(line, true);
            this.inventoryReceipt.lines.push(line);
          }
        };
        this.inventoryReceipt.taxLines = results.data.purchaseOrder.taxLines;
      }
      else if (this.vendorId) {
        this.inventoryReceipt.vendorId = this.vendorId;
        this.changeSupplier();
      }
      if (this.itemType && this.itemId) {
        const line = await this.addLine(null, this.itemType, this.itemId);
        const source = this.getSourceItem(line);
        if (source?.preferredVendor?.id) {
          this.inventoryReceipt.vendorId = source.preferredVendor.id;
          this.changeSupplier();
        }
      }
      this.inventoryReceipt.paymentMethodId = this.connectedToAccounting ? '' : null;
    }
    this.$nextTick(() => this.dirty = false);
    this.loading = false;
  },
  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.lineTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.inventoryReceipt.lines.splice(oldIndex, 1)[0];
        _self.inventoryReceipt.lines.splice(newIndex, 0, rowSelected);
      }
    })

    if (!this.inventoryReceiptId)
      this.$refs.form?.validate();
  },
  beforeDestroy() {
    this.sortable.destroy();
  },

  methods: {
    scan({id}) {
      const existingLine = this.inventoryReceipt.lines.find(line => line.type === 'Item' && line.id === id);
      if (existingLine)
        existingLine.quantity++;
      else
        this.addLine(null, 'Item', id);
    },

    itemDescription(item) {
      if (!item.type)
        return 'MISSING';
      const type = item.type === 'NonExcise' ? 'Non-excise alcohol' : item.type;
      const list = {'Product':this.products, 'Item': this.items, 'Spirit': this.spirits, 'Wine':this.wines, 'NonExcise':this.nonExcises};
      return `${type}: ${list[item.type].find(i => i.id === item.id)?.name ?? 'MISSING'}`;
    },
    tankName(tankId) {
      return this.tanks.find(tank => tank.id === tankId)?.name ?? 'Create new bulk container';
    },
    poForLine(line) {
      return this.purchaseOrdersForLine(line).find(po => po.purchaseOrderId === line.purchaseOrderId)?.poNumber ?? '';
    },
    itemTax(item) {
      if (this.connectedToAccounting && !item.taxCodeId)
        return 'Required';
      return this.taxes.find(tax => tax.id === item.taxCodeId)?.name;
    },
    async getExchangeRate() {
      if (this.vendor && this.vendor.currency !== this.company.settings.homeCurrency) {
        const results = await this.$apiCall(`query PurchasingInventoryReceiptsEdit($code:String!,$date:Date!) {
                                                currencyRate(code:$code,date:$date)
                                              }`,
                                              {code:this.vendor.currency,date:this.inventoryReceipt.date.substring(0,10)});
        if (!results.error)
          this.inventoryReceipt.exchangeRate = results.data.currencyRate;
      }
    },
    suffix(line) {
      return {'Spirit':'LAA','Wine':'litres','NonExcise':'litres','Product':'cases'}[line.type] ?? (this.items.find(i => i.id === line.id)?.buyingUnits ?? '');
    },
    precision(line) {
      return {'Spirit':3,'Wine':3,'NonExcise':3,'Product':1}[line.type] ?? (this.items.find(i => i.id === line.id)?.buyingPrecision ?? 0);
    },
    isProductNonExcise(line) {
      return this.products.find(product => product.id === line.id)?.alcohol.type === 'NonExcise' ?? false;
    },
    async itemCreated(item) {
      this.items = (await this.$store.dispatch('items')).filter(item => item.active);
      if (this.inputLine) {
        this.inputLine.type = 'Item';
        this.inputLine.id = item.id;
        this.setLineDetails(this.inputLine, false);
      }
    },
    spiritCreated(spirit) {
      this.spirits.push(spirit);
      if (this.inputLine) {
        this.inputLine.type = 'Spirit';
        this.inputLine.id = spirit.id;
        this.setLineDetails(this.inputLine, false);
      }
    },
    wineCreated(wine) {
      this.wines.push(wine);
      if (this.inputLine) {
        this.inputLine.type = 'Wine';
        this.inputLine.id = wine.id;
        this.setLineDetails(this.inputLine, false);
      }
    },
    nonExciseCreated(nonExcise) {
      this.nonExcises.push(nonExcise);
      if (this.inputLine) {
        this.inputLine.type = 'NonExcise';
        this.inputLine.id = nonExcise.id;
        this.setLineDetails(this.inputLine, false);
      }
    },
    async addLine(e, type='',id='') {
      const line = {key:Date.now(),type,id,importedAlcohol:false,productAdditionType:0,quantity:0,litres:0,kg:0,strength:7,amount:0,taxCodeId:null,purchaseOrderId:'',lot:null,tankId:''};
      await this.setLineDetails(line,false);
      this.inventoryReceipt.lines.push(line);
      this.selectedItem = line.key;
      this.$nextTick(() => this.$refs.form?.validate());
      return line;
    },
    deleteLine(index) {
      this.inventoryReceipt.lines.splice(index, 1);
      this.calculateTaxes();
    },
    async changeDate() {
      await this.getTanks();      // Refresh list of tanks
      for (const line of this.inventoryReceipt.lines) {
        await this.setLineDetails(line, false);
      }
      this.getExchangeRate();
    },
    async getTanks() {
      this.allTanks = [];
      await this.$lazyListLoad(this.allTanks, 'tanks2', `id,name,contents{siteId,volume,alcoholType,alcoholId,alcoholName}`, 100, this.inventoryReceiptId?[]:[{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.inventoryReceipt.date}]);

      // Check for valid line entries
      for (const line of this.inventoryReceipt.lines) {
        if ((line.type.startsWith('Tank') && !this.tanks.some(b => b.id === line.tankId)))
          line.tankId = '';
      }
      this.$nextTick(() => this.$refs.form?.validate());
    },
    getSourceItem(line) {
      const list = {'Item':this.items,'Spirit':this.spirits,'Wine':this.wines,'WineHigh':this.wines,'WineMedium':this.wines,'WineWeak':this.wines,'NonExcise':this.nonExcises,'Product':this.products};

      return list[line.type]?.find(item => item.id === line.id) ?? {};
    },
    async setLineDetails(line, init) {
      if (line.id === '')
        return;

      const source = this.getSourceItem(line);

      const type = line.type.charAt(0).toLowerCase() + line.type.slice(1)
      const lotTracking = (line.type === 'Item' && source.lotTracking)
                       || (line.type === 'Product' && this.company.settings.lotTrackingForProducts)
                       || line.type !== 'Item' && line.type !== 'Product';
      this.loading = true;
      const results = await this.$apiCall(`query PurchasingInventoryReceiptsEdit($id:ID!,$loaded:Boolean!,$lotTracking:Boolean!,$edit:Boolean!) {
                                              ${type}(id:$id) @skip(if:$loaded) {
                                                sku,buyingDescription,reorderQuantity,preferredVendor{id},vendors{vendorId,tax{id},cost,sku}
                                                outstandingPurchaseOrders(edit:$edit) {
                                                  purchaseOrderId,vendorId,poNumber,poDate,quantityOutstanding,unitCost,taxCodeId
                                                }
                                              }
                                              lotUnique(prefix:"${type.substring(0,1).toUpperCase()}") @include(if:$lotTracking)
                                            }`,
                                            {id:line.id,loaded:source.loaded || false,lotTracking,edit:this.inventoryReceiptId!==undefined});
      this.loading = false;
      if (!results.error) {
        if (!source.loaded) {
          Object.assign(source, results.data[type]);
          source.loaded = true;
        }
        if (lotTracking && (!init || line.lot == null))
          line.lot = results.data.lotUnique;
      }

      const vendor = source.vendors.find(vendor => vendor.vendorId === this.inventoryReceipt.vendorId);

//      if (!init)
//        line.taxCodeid = vendor?.taxCodeId || line.taxCodeId;

      line.sku = vendor?.sku || source.sku;
      line.quantity = line.quantity || source.reorderQuantity || 1;
      line.amount = init ? line.amount : (line.amount || line.quantity * (vendor?.cost ?? 0));
      this.amountChanged(line);
      line.taxCodeId = vendor?.tax.id || line.taxCodeId;
      if (!init && this.inventoryReceipt.taxInclusive && line.taxCodeId) {
        const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
        line.unitPrice += line.unitPrice * tax.purchaseRate/100;
        line.amount = this.$roundNumber(line.unitPrice * line.quantity);
      }
      this.calculateTaxes();
      this.$nextTick(() => this.$refs.form?.validate());
    },
    toggleTaxInclusive() {
      for (const line of this.inventoryReceipt.lines) {
        if (line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.inventoryReceipt.taxInclusive)
            line.unitPrice += line.unitPrice * tax.purchaseRate/100;
          else
            line.unitPrice = line.unitPrice / (1 + tax.purchaseRate/100)
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.calculateTaxes();
    },
    purchaseOrdersForLine(line) {
      return [{purchaseOrderId:'',poNumber:''},...this.getSourceItem(line)?.outstandingPurchaseOrders?.filter(po => po.vendorId === this.inventoryReceipt.vendorId) ?? []];
    },
    changedPO(line) {
      const po = this.purchaseOrdersForLine(line).find(po => po.purchaseOrderId === line.purchaseOrderId);
      line.quantity = po.quantityOutstanding ?? line.quantity;
      line.amount = po.unitCost ? line.quantity * po.unitCost : line.amount;
      this.amountChanged(line);
      line.taxCodeId = po.taxCodeId ?? line.taxCodeId;
    },
    updateAmount(line) {
      const source = this.getSourceItem(line);
      const vendor = source && source.vendors.find(vendor => vendor.vendorId === this.inventoryReceipt.vendorId);
      line.amount = line.quantity * (vendor ? vendor.cost : line.unitPrice);
      this.calculateTaxes();
    },
    amountChanged(line) {
      line.unitPrice = line.amount ? line.amount / line.quantity : 0;
      this.calculateTaxes();
    },
    async newSupplier(supplier) {
      this.vendors.push(supplier);
      this.inventoryReceipt.vendorId = supplier.id;
      this.getExchangeRate();
    },
    async changeSupplier() {
      if (!this.vendor) {
        this.inventoryReceipt.vendorAddress = '';
        this.inventoryReceipt.accountRef = '';
        return;
      }

      if (!this.vendor.suppliesInBond)
        this.inventoryReceipt.lines = this.inventoryReceipt.lines.filter(line => line.type === 'Item' || line.type.includes('non-excise'));

      if (!this.vendor.loaded) {
        this.loading = true;
        const results = await this.$apiCall(`query PurchasingInventoryReceiptsEdit($id:ID!) {
                                                vendor(id:$id) {
                                                  id,name,currency,
                                                  billingAddress{line1,line2,city,province,postalCode}
                                                }
                                              }`,
                                              {id:this.vendor.id});
        this.loading = false;
        if (!results.error) {
          this.vendor.loaded = true;
          Object.assign(this.vendor, results.data.vendor);
        }
      }
      const address = this.vendor.billingAddress;
      this.inventoryReceipt.vendorAddress = this.vendor.name + '\n' + address.line1 + '\n' + (address.line2 ? address.line2 + '\n' : '') +
                              address.city + ' ' + address.province + '  ' + address.postalCode;
      this.inventoryReceipt.accountRef = this.eligibleAccounts.length > 0 ? this.eligibleAccounts[0].ref : '';
      for (const line of this.inventoryReceipt.lines)
        this.setLineDetails(line, false);
      this.calculateTaxes();
      this.getExchangeRate();
    },
    cancel() {
      this.$router.back();
    },
    isLineIncomplete(line) {
      return !line.type || !line.id ||
              line.quantity < 0 && line.type !== 'Item' ||
              line.litres < 0 && line.type === 'Spirit' ||
              line.kg < 0 && line.type === 'Spirit' ||
              (line.strength < 0 || line.strength > 100) && (line.type === 'Spirit' || line.type === 'Wine') ||
              line.lot != null && line.lot === '' ||
              line.amount < 0 && line.type !== 'Item' ||
              (this.connectedToAccounting && !line.taxCodeId);
    },
    calculateTaxes() {
      this.inventoryReceipt.taxLines = [];
      this.inventoryReceipt.lines.forEach(line => {
        if (line.taxCodeId) {
          let tax = this.inventoryReceipt.taxLines.find(tax => tax.id === line.taxCodeId);
          if (!tax) {
            const t = this.taxes.find(t => t.id === line.taxCodeId);
            tax = {id:line.taxCodeId,name:t.name,percent:t.purchaseRate,amount:0,onAmount:0};
            this.inventoryReceipt.taxLines.push(tax);
          }
          tax.onAmount += this.inventoryReceipt.taxInclusive ? line.amount/(1+tax.percent/100) : line.amount;
          tax.amount += this.$roundNumber(this.inventoryReceipt.taxInclusive ? line.amount - line.amount/(1+tax.percent/100) : line.amount * tax.percent / 100);
        }
      });
    },
    async save() {
      if (!this.$refs.form.validate() || this.inventoryReceipt.lines.some(line => this.isLineIncomplete(line))) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {lines,pendingReview,attachmentURL,qbo,recurringTransaction,...input} = this.inventoryReceipt;
      input.lines = this.inventoryReceipt.lines.map(line => {
        const importedAlcohol = line.type === 'Product' ? line.productAdditionType === 1 : line.importedAlcohol;
        const productReturn = line.type === 'Product' && line.productAdditionType === 2;

        let amount = line.amount;
        if (input.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          amount = this.$roundNumber(line.amount / (1 + tax.purchaseRate/100));
        }

        return {id:line.id,tankId:line.tankId||null,quantity:line.quantity,amount,litres:line.litres,kg:line.kg,strength:line.strength,
                purchaseOrderId:line.purchaseOrderId,
                taxCodeId:line.taxCodeId,lot:line.lot,
                importedAlcohol,productReturn};
      });

      // If copying or existing inventory receipt is made recurring, create a new recurring inventory receipt (and leave original unchanged)
      if (this.copyId || this.recurring && !this.inventoryReceipt.recurringTransaction)
        delete input.id;

      if (this.recurring)
        input.recurringTransaction = this.recurringTransaction;
      input.fromRecurringTransactionId = this.fromRecurringTransactionId;

      const results = await this.$apiCall(`mutation PurchasingInventoryReceiptsEdit($input: InventoryReceiptInput!) {
                                             inventoryReceiptCreate(input:$input) {id}
                                           }`,
                                          {input})

      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        if (this.recurring)
          this.$store.commit('setError', {type:'success',message:`${input.id?'Updated':'Created'} recurring inventory receipt template from ${this.vendor.name}`});
        else
          this.$store.commit('setError', {type:'success',message:`${input.id?'Updated':'Created'}${this.inventoryReceipt.draft?' draft':''} inventory receipt from ${this.vendor.name}`});
      }
    }
  }
}
</script>